export interface CaseSection {
  id: string;
  heading: string;
  paragraphs: string[];
  points?: string[];
}

export interface CaseItem {
  id: string;
  title: string;
  category: string;
  categoryLabel: string;
  summary: string;
  cover: string;
  coverAlt: string;
  author: string;
  date: string;
  updated: string;
  readTime: string;
  tags: string[];
  heat: number;
  featured: boolean;
  sections: CaseSection[];
  risks: string[];
  tips: string[];
}

export const cases: CaseItem[] = [
  {
    id: "renovation-hidden-cost",
    title: "「低价签约、后期增项」：装修报价单里最先被动手脚的三处",
    category: "industry",
    categoryLabel: "行业潜规则",
    summary:
      "同一套房，三家报价能差出四成。差价不在材料，而在报价单刻意留白的三个位置。我们按签约到竣工的完整顺序，还原增项是怎么一步步加上去的。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20editorial%20abstract%20photograph%20of%20folded%20blueprint%20papers%20on%20a%20textured%20ink%20black%20surface%20with%20a%20single%20deep%20crimson%20light%20beam%20across%20the%20frame%2C%20cinematic%20shadows%2C%20investigative%20documentary%20atmosphere%2C%20no%20text&width=800&height=600&seq=case-renovation-01&orientation=landscape",
    coverAlt: "折叠的装修报价单与图纸在暗色桌面上，红色光线斜切而过",
    author: "调查组 · 老陈",
    date: "2025-11-18",
    updated: "2025-12-02",
    readTime: "9 分钟",
    tags: ["装修", "合同陷阱", "增项", "报价单"],
    heat: 9820,
    featured: true,
    sections: [
      {
        id: "s1",
        heading: "报价单为什么总是「刚好」比别家便宜",
        paragraphs: [
          "先要理解装修公司的利润结构：主材和人工的价格在市场上其实很透明，真正能拉开差距的是「没写进合同的那部分」。一份看起来便宜四成的报价单，往往不是材料更便宜，而是故意少算了几个必然会发生的项目。",
          "最常见的留白位置是：拆改与垃圾清运、水电改造的实际米数、以及墙面找平的基层处理。这三项的共同点是——无法在开工前给出精确数字，只能写「按实际发生量结算」。",
        ],
        points: [
          "拆改与垃圾清运：报价单里只写「拆墙」，不写清运和楼层搬运费",
          "水电改造：写单价不写预估总米数，完工后按实际测量结算",
          "基层处理：写「含找平」，但不含局部空鼓的加固处理",
        ],
      },
      {
        id: "s2",
        heading: "增项是在哪个瞬间被「合法化」的",
        paragraphs: [
          "开工第一天，工长通常会当着你的面指出一个「必须马上处理」的问题，比如墙面空鼓、卫生间防水层老化、承重墙外的非承重墙需要加固。这时候工程已经启动，你很难喊停，而对方给出的方案通常是「先做，最后一起算」。",
          "这句话就是增项的入口。一旦你口头同意「先做」，后续所有追加就都有了依据，因为合同里那句「按实际发生量结算」会被反复引用。",
        ],
      },
      {
        id: "s3",
        heading: "付款节点和水电改造的绑定关系",
        paragraphs: [
          "多数报价单把付款拆成四到五个节点，其中「水电验收后支付 35%」是最容易被利用的一档。因为水电改造的最终米数直到封槽前才能确认，而这个节点往往设计在所有增项浮出水面之后。",
          "换句话说，当你意识到总价涨了，你已经付掉了六成以上。剩下的谈判筹码，比你想象中少得多。",
        ],
      },
      {
        id: "s4",
        heading: "懂行的人签约前会做的三件事",
        paragraphs: [
          "第一，要求把「按实际发生量结算」改成「超出预算 5% 以上的部分需书面确认后方可施工」。第二，让工长在开工前上门测量一次，把水电预估米数写进合同。第三，把每一个「含」字后面的内容，都追问一句「含到什么程度」。",
          "这三件事不需要你懂装修，只需要你在签字之前多问几个问题。对方如果含糊其辞，本身就说明了一些东西。",
        ],
      },
    ],
    risks: [
      "开工后被指出「必须马上处理」的问题，此时停工成本高，容易被牵着走",
      "「按实际发生量结算」条款会在纠纷中成为对方的主要依据",
      "付款节点若集中在增项暴露之前，后期几乎没有议价空间",
    ],
    tips: [
      "要求书面确认门槛：超出预算一定比例需你签字才可施工",
      "开工前索取水电改造的预估米数并写入合同附件",
      "把每一句「含 XX」追问到具体范围和工艺标准",
      "保留所有口头承诺的聊天记录与录音，作为后续对照依据",
    ],
  },
  {
    id: "used-car-odometer",
    title: "调表车的三个破绽：为什么里程表最不可信",
    category: "industry",
    categoryLabel: "行业潜规则",
    summary:
      "里程表是二手车价格最敏感的变量，也是最容易被修改的数据。我们梳理了评估环节真正在看的几个位置，以及买家自己能低成本核对的部分。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20cinematic%20abstract%20photograph%20of%20a%20car%20dashboard%20odometer%20glowing%20faintly%20in%20deep%20shadow%20with%20amber%20and%20crimson%20rim%20light%2C%20moody%20industrial%20garage%20atmosphere%2C%20grainy%20editorial%20texture%2C%20no%20text&width=800&height=600&seq=case-usedcar-01&orientation=landscape",
    coverAlt: "暗色车库中汽车仪表盘里程表微微发光，琥珀色轮廓光",
    author: "调查组 · 阿峰",
    date: "2025-10-29",
    updated: "2025-11-20",
    readTime: "8 分钟",
    tags: ["二手车", "调表", "验车", "信息不对称"],
    heat: 8740,
    featured: true,
    sections: [
      {
        id: "s1",
        heading: "调表不是改数字，而是改一整套记录",
        paragraphs: [
          "只改仪表盘上的数字，几秒钟就能做，但那种做法太容易被识破。更常见的操作是把里程数「理顺」：让它和保养记录、轮胎磨损、内饰使用痕迹大致吻合，形成一个说得通的整体。",
          "所以判断调表，重点不是看仪表盘，而是找那些不容易被同步修改的第三方痕迹。",
        ],
      },
      {
        id: "s2",
        heading: "真正难改的三处痕迹",
        paragraphs: [
          "第一是刹车踏板的橡胶磨损，它和驾驶习惯、使用年限强相关，更换成本不低，很少有人会为了配合调表去换。第二是保养系统里的历史工单，4S 体系内的记录带时间戳，跨店也查得到。",
          "第三是保险理赔与年检记录里的里程快照。很多车在出险或年检时会留下当时的公里数，几个时间点连起来，行驶曲线就出来了。",
        ],
        points: [
          "踏板、挡把、座椅侧翼的磨损程度与表显里程是否匹配",
          "保养工单的时间与里程是否呈现合理的递增曲线",
          "出险、年检记录中的里程快照是否存在矛盾",
        ],
      },
      {
        id: "s3",
        heading: "卖家最怕你问的那句话",
        paragraphs: [
          "「这辆车能不能一起去看一下最近两次保养的记录？」这句话之所以有效，是因为它要求对方提供可核验的第三方材料，而不是口头保证。愿意配合的人，通常问题不大。",
          "如果对方用「车主不是我」「记录在我朋友那」来推脱，你不需要争辩，只需要把价格假设为「高里程」重新谈，或者直接换一台。",
        ],
      },
    ],
    risks: [
      "低价往往对应高里程或事故史，价格异常本身就是信号",
      "口头承诺的「原版原漆」「一手车」很难作为后续维权依据",
      "过户前的第三方检测若由卖家指定，独立性存疑",
    ],
    tips: [
      "要求查看保养记录、出险记录与年检里程快照",
      "自行指定第三方检测机构，不接受卖家安排",
      "把里程、事故、过户次数等承诺写进购车合同",
      "保留看车时的照片与沟通记录，标注拍摄时间",
    ],
  },
  {
    id: "livestream-price-trick",
    title: "直播间的「限量秒杀」：价格是先涨上去，再降下来的",
    category: "consumer",
    categoryLabel: "消费陷阱",
    summary:
      "「原价 899，直播专享 199」是大家最熟悉的一幕。我们把一场典型直播的价格节奏拆开看，涨价、上链接、倒计时分别发生在什么时间点。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20a%20ring%20light%20glowing%20in%20an%20empty%20dark%20studio%20with%20deep%20crimson%20and%20amber%20light%20bleed%2C%20cinematic%20commercial%20backstage%20atmosphere%2C%20high%20contrast%20editorial%20texture%2C%20no%20text&width=800&height=600&seq=case-livestream-01&orientation=landscape",
    coverAlt: "空荡暗色直播棚里的环形补光灯，红色与琥珀色光晕",
    author: "调查组 · 小满",
    date: "2025-10-12",
    updated: "2025-11-05",
    readTime: "7 分钟",
    tags: ["直播带货", "价格套路", "促销", "冲动消费"],
    heat: 9130,
    featured: true,
    sections: [
      {
        id: "s1",
        heading: "倒计时不是催你，是压缩你查价的时间",
        paragraphs: [
          "直播间的核心变量是「决策时间」。所有设计——倒计时、限量库存、连麦喊单——都在做同一件事：把用户从「我要比较一下」推向「再不下单就没了」。",
          "在这个时间窗口里，用户很难离开页面去比价，而这正是价格操作得以成立的前提。",
        ],
      },
      {
        id: "s2",
        heading: "价格的三段式节奏",
        paragraphs: [
          "第一步是提前几周把商品的原价在平台上「挂高」，形成历史价参照。第二步是在开播前用一段时间维持这个高价，让价格曲线看起来是真的。第三步才是直播当天的「骨折价」。",
          "所以直播间里那个被划掉的「原价」，往往从未真正成交过，它只是一个用来制造落差的数字。",
        ],
        points: [
          "用历史价格工具查看近 90 天的真实成交价区间",
          "关注「优惠券可用范围」是否被临时收窄",
          "把心仪商品提前加入收藏，隔几天再看一次价格",
        ],
      },
      {
        id: "s3",
        heading: "「限量」是怎么被制造的",
        paragraphs: [
          "库存数字并不总是与真实供货量一致，它可以被阶段性上调或下调以制造紧迫感。真正稀缺的是「这个价格能维持多久」，而不是商品本身。",
          "一个实用的判断方法：如果一个商品在多个不同主播的直播间反复「限量秒杀」，那它大概率不稀缺，稀缺的只是你的耐心。",
        ],
      },
    ],
    risks: [
      "被划掉的原价可能从未真实成交，落差感是被设计出来的",
      "倒计时与库存数字会压缩比价时间，导致冲动下单",
      "部分优惠券设置使用范围，结算时才发现不可用",
    ],
    tips: [
      "下单前用历史价格工具核对近 90 天成交区间",
      "把商品先收藏，隔 2 到 3 天再看一次真实价格",
      "结算前确认优惠券适用范围与退款政策",
      "对反复「限量秒杀」的商品保持警惕",
    ],
  },
  {
    id: "job-deposit-scam",
    title: "入职前要你交钱：押金、内推费与「培训费」的完整话术链",
    category: "career",
    categoryLabel: "职场招聘",
    summary:
      "正规用人单位不会向员工收费，这是常识。但套路会把这个常识包装成「你敢不敢投入」。我们按一次典型招聘的时间线，复现每一句话术的作用。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20a%20blank%20employment%20contract%20and%20a%20pen%20on%20an%20ink%20black%20desk%20lit%20by%20a%20narrow%20crimson%20spotlight%2C%20cinematic%20noir%20atmosphere%2C%20editorial%20grain%20texture%2C%20no%20text&width=800&height=600&seq=case-job-01&orientation=landscape",
    coverAlt: "暗色桌面上的一份空白入职合同与一支笔，红色聚光灯",
    author: "调查组 · 林默",
    date: "2025-09-24",
    updated: "2025-10-30",
    readTime: "8 分钟",
    tags: ["招聘", "押金", "求职陷阱", "劳动合同"],
    heat: 8210,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "收费的三种说法，指向同一个目的",
        paragraphs: [
          "第一种说法是「服装押金」「工牌押金」，金额不大，让你觉得不值得计较。第二种是「内推服务费」，把收费包装成帮你介绍资源。第三种是「岗前培训费」，先说培训后上岗，再说费用从工资里扣。",
          "三种说法的共同点是：钱先离开你，而承诺永远在未来。",
        ],
        points: [
          "服装押金 / 工牌押金",
          "内推服务费 / 推荐费",
          "岗前培训费 / 资料费 / 体检费由本人全额垫付",
        ],
      },
      {
        id: "s2",
        heading: "为什么流程看起来那么「正规」",
        paragraphs: [
          "为了让人放下戒备，这类招聘会把流程做得很完整：有面试、有工牌、有办公场地、有钉钉群。甚至真的会安排一两天培训，让你产生「已经在上班了」的错觉。",
          "关键破绽往往不在流程，而在细节：公司名称与签合同的主体不一致、办公场地是短租、HR 从不使用企业邮箱。",
        ],
      },
      {
        id: "s3",
        heading: "把钱守住的最简规则",
        paragraphs: [
          "规则只有一条：任何以入职为条件、要求你先付钱的环节，都直接拒绝。不需要解释，也不需要讨价还价。",
          "如果你已经付了，尽快保留转账记录、聊天记录与对方身份信息，向当地劳动保障部门反映。金额大小不影响你是否该维权。",
        ],
      },
    ],
    risks: [
      "以入职为条件的任何前置收费都是高危信号",
      "合同主体与招聘主体不一致时，后续几乎无法追责",
      "短租办公场地与个人账户收款是常见特征",
    ],
    tips: [
      "任何入职前收费一律拒绝，没有例外",
      "核对合同签约主体与招聘公司的工商信息是否一致",
      "收款账户若为个人账户，立即停止流程",
      "保留转账与聊天记录，及时向劳动保障部门反映",
    ],
  },
  {
    id: "medical-beauty-card",
    title: "医美「办卡分期」：你签的可能是贷款，而不是会员卡",
    category: "consumer",
    categoryLabel: "消费陷阱",
    summary:
      "从咨询到刷卡，真正决定你口袋深浅的不是项目价格，而是那台放在前台的分期设备。我们把签约现场的流程逐句拆开。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20a%20payment%20terminal%20and%20a%20folded%20membership%20card%20on%20a%20dark%20marble%20counter%20with%20soft%20crimson%20edge%20light%2C%20cinematic%20clinical%20atmosphere%2C%20editorial%20texture%2C%20no%20text&width=800&height=600&seq=case-beauty-01&orientation=landscape",
    coverAlt: "暗色大理石台面上的刷卡机与折叠会员卡，红色边缘光",
    author: "调查组 · 苏晴",
    date: "2025-09-08",
    updated: "2025-10-18",
    readTime: "7 分钟",
    tags: ["医美", "预付费", "分期", "消费贷"],
    heat: 7680,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "咨询室里的价格锚点",
        paragraphs: [
          "咨询环节会先给出一个高于你预期很多的总价，再顺势推出「会员价」或「套餐价」，落差本身就在推动你接受分期。",
          "此时真正需要你确认的不是项目，而是付款方式——因为一旦走分期，你面对的是金融机构，而不是这家门店。",
        ],
      },
      {
        id: "s2",
        heading: "分期协议里最该看清的三行字",
        paragraphs: [
          "第一行是放款主体，也就是你把钱欠给了谁。第二行是分期期数与总利息。第三行是提前还款条款，很多产品提前还款仍需支付全部利息。",
          "如果门店用「帮你在系统里点一下」来操作，请务必自己看完这几行再确认。",
        ],
        points: [
          "放款主体是谁，是否为你听说过或查得到的机构",
          "分期总期数与实际总利息金额",
          "提前还款是否需要支付全部剩余利息",
        ],
      },
      {
        id: "s3",
        heading: "门店跑路之后，债还在你身上",
        paragraphs: [
          "预付费最大的风险在于资金与服务的分离：钱已经一次性付给门店或放款机构，而服务需要未来几个月才能兑现。门店一旦停止经营，服务没了，分期还款却不会停。",
          "所以决定要不要办卡前，值得先问一句：这些项目我能不能按次单独购买。如果能，就不要用「以后常来」说服自己。",
        ],
      },
    ],
    risks: [
      "分期放款主体为金融机构，门店停止经营后债务仍在",
      "提前还款可能仍需支付全部剩余利息",
      "预付费金额与服务兑现存在明显时间差",
    ],
    tips: [
      "付款前确认项目是否可按次单买，优先选择单次结算",
      "分期前逐行阅读放款主体、期数与提前还款条款",
      "不要在现场压力下当场签字，带走合同回去看",
      "保留项目报价单与所有沟通记录",
    ],
  },
  {
    id: "low-price-tour",
    title: "低价团的盈利逻辑：团费不是收入，购物返点才是",
    category: "consumer",
    categoryLabel: "消费陷阱",
    summary:
      "「零团费」「低价团」之所以能存在，是因为旅行社的利润来源根本不是团费。我们按一条线路的三天行程，标出返点从哪里来。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20cinematic%20abstract%20photograph%20of%20an%20empty%20tour%20bus%20aisle%20at%20night%20with%20amber%20ceiling%20lights%20and%20a%20single%20crimson%20glow%20at%20the%20far%20end%2C%20moody%20documentary%20atmosphere%2C%20grainy%20editorial%20texture%2C%20no%20text&width=800&height=600&seq=case-tour-01&orientation=landscape",
    coverAlt: "夜间空荡的旅游大巴过道，琥珀色顶灯与远处的红色光点",
    author: "调查组 · 老周",
    date: "2025-08-19",
    updated: "2025-09-26",
    readTime: "8 分钟",
    tags: ["旅游", "低价团", "购物返点", "强制消费"],
    heat: 7020,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "团费的定价逻辑是「亏本引流」",
        paragraphs: [
          "低价团的团费通常低于机票、酒店和用车的成本总和，也就是说旅行社在出发前就是亏的。这部分亏空，需要在行程中的购物环节补回来。",
          "理解了这一点，行程表上那些「顺路参观」的珠宝、玉石、乳胶、保健品店，性质就清楚了。",
        ],
      },
      {
        id: "s2",
        heading: "返点是怎么分的",
        paragraphs: [
          "购物店会把成交金额的一部分返还给旅行社和导游，这就是地接社的利润来源。为了让这笔利润最大化，行程会被设计成「店多景少」，真正游玩的时间被压缩。",
          "导游的收入结构也决定了压力传导的方向：底薪很低，主要靠返点，所以催促消费的动力来自她自己的账本。",
        ],
        points: [
          "行程表上购物店的数量与停留时长",
          "「自愿消费」在实际行程中是否真的自愿",
          "自费项目的定价与市场价差距",
        ],
      },
      {
        id: "s3",
        heading: "签约前能做的三件事",
        paragraphs: [
          "第一，要求把购物店数量、名称和停留时长写进合同。第二，确认团费是否包含景区门票与必要交通。第三，看清「不可抗力」与「行程调整」条款，这两条常被用来解释临时加店。",
          "如果一份低价团合同里对购物环节只字未提，那更需要谨慎。",
        ],
      },
    ],
    risks: [
      "低价团团费低于成本，利润依赖行程中的购物返点",
      "购物店数量与停留时长不写入合同时难以维权",
      "导游收入与返点挂钩，容易形成消费压力",
    ],
    tips: [
      "签约前要求写明购物店数量、名称与停留时长",
      "确认团费是否含门票、交通等必要项目",
      "优先选择行程中购物环节透明的产品",
      "对明显低于成本的报价保持警惕",
    ],
  },
  {
    id: "health-seminar-script",
    title: "会销现场的情感剧本：为什么老人总是最后掏钱的那一个",
    category: "consumer",
    categoryLabel: "消费陷阱",
    summary:
      "保健品会销的核心不是产品，而是一套经过反复打磨的情感节奏。我们按三天会议的流程，标出每一步把关系拉近的做法。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20an%20empty%20conference%20room%20with%20rows%20of%20chairs%20and%20a%20dim%20amber%20stage%20light%2C%20cinematic%20solitary%20atmosphere%2C%20editorial%20grain%2C%20deep%20shadows%2C%20no%20text&width=800&height=600&seq=case-seminar-01&orientation=landscape",
    coverAlt: "昏暗会议室里成排的空椅子与琥珀色舞台灯",
    author: "调查组 · 何姐",
    date: "2025-08-02",
    updated: "2025-09-12",
    readTime: "8 分钟",
    tags: ["保健品", "会销", "老年消费", "情感营销"],
    heat: 6540,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "第一天：免费礼品和一次免费体检",
        paragraphs: [
          "会销的第一步是降低参与门槛。「免费领鸡蛋」「免费量血压」这类由头，目的是把人请到现场，并留下联系方式。",
          "接着安排一次免费的「健康检测」，结果往往人人都有点问题，这就为后续的产品铺垫了理由。",
        ],
      },
      {
        id: "s2",
        heading: "第二天：把销售员变成「干女儿」",
        paragraphs: [
          "销售人员会在这天集中做情感投入：记住每个人的名字、健康状况和家庭情况，主动帮忙拎东西、陪聊。对独居老人来说，这种陪伴是有真实价值的，也正是它最有效的原因。",
          "关系一旦建立，后面的推荐就不再像推销，而像「孩子的一片孝心」。",
        ],
        points: [
          "销售员是否频繁以「关心身体健康」为由上门或频繁来电",
          "是否出现认亲式称呼与过度情感投入",
          "是否以「名额有限」「今天最后一天」催促当场决定",
        ],
      },
      {
        id: "s3",
        heading: "第三天：把决定权交还给你，但只剩一个选项",
        paragraphs: [
          "最后一天通常安排「专家」到场，配合现场下单的从众氛围。表面上是把选择权给你，实际上通过限时、限量、老客户带头的方式，把选项压缩到只剩一个。",
          "如果家里长辈已经参与类似活动，硬性阻止往往适得其反。更有效的做法是陪他们一起去查产品批号，把判断权变成共同的事。",
        ],
      },
    ],
    risks: [
      "免费体检的结果通常被用作产品推荐依据",
      "高强度的情感投入会削弱老年人的判断力",
      "限时限量与从众氛围压缩了犹豫和核实的时间",
    ],
    tips: [
      "遇到免费体检和赠品活动先了解组织方资质",
      "对产品的批准文号与宣传功效做一次公开查询",
      "家人陪同参与，把判断过程变成共同决策",
      "警惕当天必须付款、名额有限的话术",
    ],
  },
  {
    id: "loan-service-fee",
    title: "「砍头息」的变体：服务费是怎么从本金里被先扣走的",
    category: "gray",
    categoryLabel: "灰色地带",
    summary:
      "借 10 万到手 8 万，剩下的被各种名目先扣掉。我们整理了几种常见的费用包装方式，以及合同里把它们藏起来的位置。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20cinematic%20abstract%20photograph%20of%20stacked%20blank%20documents%20and%20a%20calculator%20on%20an%20ink%20black%20surface%20lit%20by%20a%20thin%20crimson%20light%2C%20moody%20financial%20noir%20atmosphere%2C%20editorial%20texture%2C%20no%20text&width=800&height=600&seq=case-loan-01&orientation=landscape",
    coverAlt: "暗色桌面上的成叠文件与计算器，细窄红色光线",
    author: "调查组 · 老陈",
    date: "2025-07-21",
    updated: "2025-08-30",
    readTime: "9 分钟",
    tags: ["借贷", "砍头息", "服务费", "合同条款"],
    heat: 7180,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "换个名字，费用就从「利息」变成了「服务费」",
        paragraphs: [
          "监管对利率有明确上限，于是一些产品把超出部分改名为「服务费」「管理费」「咨询费」，让综合成本看起来合规。",
          "判断成本高低，不能只看合同里写的年化利率，而要看「实际到手金额」和「实际还款总额」这两个数字。",
        ],
        points: [
          "服务费 / 管理费 / 咨询费 / 平台使用费",
          "保险费由借款人承担并一次性扣除",
          "以「保证金」名义先扣后退的条款",
        ],
      },
      {
        id: "s2",
        heading: "这些费用藏在合同的哪里",
        paragraphs: [
          "它们通常不在利率条款附近，而是分散在「费用与收费」的附件、产品说明页的灰色小字，以及签约时快速划过的电子协议里。",
          "一个简单方法：拿到合同后只看两件事——放款金额和到账金额是否一致；以及所有需要你支付的费用项加起来是多少。",
        ],
      },
      {
        id: "s3",
        heading: "已经借了怎么办",
        paragraphs: [
          "先做一件事：把所有还款记录、扣费流水和合同完整保存下来，算出真实的综合成本。很多纠纷的突破口，就在于「实际到手金额」与合同本金之间的差额。",
          "涉及具体利率是否超出的问题，需要专业法律意见，不要仅凭某篇文章或某位网友的说法自行定性。",
        ],
      },
    ],
    risks: [
      "综合成本不能只看名义利率，需看实际到手金额",
      "费用条款分散在附件与电子协议中，容易被忽略",
      "以保证金名义先扣后退的条款存在退还风险",
    ],
    tips: [
      "签约前确认放款金额与实际到账金额是否一致",
      "把合同中所有费用项相加，算出真实综合成本",
      "完整保存合同、流水与扣费记录",
      "涉及利率争议时咨询专业法律意见",
    ],
  },
  {
    id: "review-manipulation",
    title: "评分为何不可信：刷单与控评的日常操作方式",
    category: "backroom",
    categoryLabel: "暗箱操作",
    summary:
      "一家新开的店如何在两周内刷到高分？差评又是怎么一个个「消失」的？我们梳理了评价体系被干预的几种常见方式。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20a%20wall%20of%20blurred%20glowing%20star%20shapes%20in%20deep%20shadow%20with%20crimson%20and%20amber%20accents%2C%20cinematic%20digital%20atmosphere%2C%20editorial%20grain%20texture%2C%20no%20text&width=800&height=600&seq=case-review-01&orientation=landscape",
    coverAlt: "暗色背景上一片模糊发光的星形图案，红色与琥珀色点缀",
    author: "调查组 · 小满",
    date: "2025-07-05",
    updated: "2025-08-11",
    readTime: "7 分钟",
    tags: ["刷单", "控评", "评价体系", "数据造假"],
    heat: 6890,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "评分的速度本身就是线索",
        paragraphs: [
          "真实的好评需要顾客实际到店、体验、再花时间写出来，因此增长是缓慢且有波动的。如果一家店在短时间内从无到有积累了几十条格式相似的好评，这本身就值得留意。",
          "观察评价的时间分布，比看总分更有用。",
        ],
        points: [
          "评价是否集中在极短的时间内爆发式增长",
          "好评的语言风格是否高度相似、缺少具体细节",
          "差评是否集中在同一时间段内被大量删除或折叠",
        ],
      },
      {
        id: "s2",
        heading: "控评的几种做法",
        paragraphs: [
          "常见方式包括：用免单或返现换取好评并附条件删除差评；批量举报差评使其被平台折叠；以及用新账号发布大量正面内容稀释负面信息的可见度。",
          "这些操作很难被用户直接看到，但结果会体现在「评分与实际体验不符」上。",
        ],
      },
      {
        id: "s3",
        heading: "怎么判断一家店值不值得去",
        paragraphs: [
          "比起总分，更值得看的是差评里的具体内容：是否提到同一个问题、商家的回复态度如何。另外，带图片的长评价、提及具体菜品或服务细节的评价，参考价值远高于一句「很好」。",
          "如果有条件，直接电话咨询一些细节，真实的店家通常能给出准确答复。",
        ],
      },
    ],
    risks: [
      "短期爆发的好评可能是集中操作的结果",
      "差评被折叠或删除会掩盖服务问题",
      "仅凭总分判断容易与真实体验产生偏差",
    ],
    tips: [
      "关注评价的时间分布与语言相似度",
      "重点阅读带图长评与差评中的具体描述",
      "留意商家对差评的回复态度与处理方式",
      "下单前通过电话核实关键信息",
    ],
  },
  {
    id: "training-refund",
    title: "退费难是怎么被设计出来的：教培合同里的四个卡点",
    category: "consumer",
    categoryLabel: "消费陷阱",
    summary:
      "报名时口口声声「随时可退」，真要退时却处处为难。我们把退费流程上的四个卡点按顺序标出来，方便你在签约前逐个确认。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20cinematic%20abstract%20photograph%20of%20a%20thick%20contract%20booklet%20with%20dog%20eared%20pages%20on%20an%20ink%20black%20desk%20under%20a%20narrow%20amber%20beam%2C%20moody%20editorial%20atmosphere%2C%20grainy%20texture%2C%20no%20text&width=800&height=600&seq=case-training-01&orientation=landscape",
      coverAlt: "暗色桌面上卷角的厚合同册，一束琥珀色光",
    author: "调查组 · 林默",
    date: "2025-06-16",
    updated: "2025-07-28",
    readTime: "8 分钟",
    tags: ["教培", "退费", "合同陷阱", "预付费"],
    heat: 6320,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "卡点一：把「已上课时」的定义写宽",
        paragraphs: [
          "合同里对「已消耗课时」的界定，直接决定你能退回多少钱。如果试听课、资料发放、甚至开课前的评估都算作已消耗，那么你真正开始上课时，课时已经被扣掉了一部分。",
          "报名前，值得先把「什么算已消耗」这一条问清楚。",
        ],
      },
      {
        id: "s2",
        heading: "卡点二：手续费与违约金的比例",
        paragraphs: [
          "第二个卡点是退费时要扣除的手续费或违约金。有些合同把比例设得偏高，导致退费金额所剩无几，从而降低你退款的意愿。",
          "这一条通常写得很小，但它是退费纠纷里最关键的数字。",
        ],
        points: [
          "退费手续费 / 违约金的计算比例与上限",
          "是否设置退费时间窗口（如开课 7 天后不予退费）",
          "赠品、教材是否按原价折算扣除",
        ],
      },
      {
        id: "s3",
        heading: "卡点三与四：流程冗长，以及主体不一致",
        paragraphs: [
          "第三个卡点是把退费流程设计得足够长，需要多级审批、反复填表，让你在等待中消耗耐心。第四个卡点是签约主体与收款主体不一致，导致你找谁退都说不清。",
          "签约前确认合同盖章主体、收款账户名称是否一致，能避开其中很大一部分麻烦。",
        ],
      },
    ],
    risks: [
      "「已消耗课时」的宽泛定义会大幅降低退费金额",
      "高比例违约金与短退费窗口压缩了退款空间",
      "签约主体与收款主体不一致会增加维权难度",
    ],
    tips: [
      "签约前明确「已消耗课时」的界定标准",
      "确认退费手续费比例与退费时间窗口",
      "核对合同盖章主体与收款账户名称是否一致",
      "要求把口头承诺的退费规则写入合同附件",
    ],
  },
  {
    id: "bidding-companion",
    title: "「陪标」是怎么运作的：一场招标里谁真正在决定结果",
    category: "power",
    categoryLabel: "权力内幕",
    summary:
      "有些招标在开标前结果就定了。我们梳理了陪标、围标中常见的角色分工与表面流程，帮助你理解为什么「流程完整」不等于「竞争充分」。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20photograph%20of%20sealed%20envelopes%20stacked%20on%20a%20long%20empty%20conference%20table%20lit%20by%20a%20single%20crimson%20overhead%20lamp%2C%20cinematic%20bureaucratic%20atmosphere%2C%20deep%20shadows%2C%20no%20text&width=800&height=600&seq=case-bidding-01&orientation=landscape",
    coverAlt: "长会议桌上成叠的密封信封，一盏红色顶灯",
    author: "调查组 · 老陈",
    date: "2025-05-27",
    updated: "2025-07-02",
    readTime: "9 分钟",
    tags: ["招投标", "围标", "陪标", "程序合规"],
    heat: 5980,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "「程序完整」为什么仍然可能是走过场",
        paragraphs: [
          "公开招标的流程本身是严格的：资格预审、技术标、商务标、专家评审，每一步都有文件留痕。问题往往不在流程，而在参与者的构成。",
          "如果参与投标的公司本就同属一个圈子，那么再严谨的流程也只是在几家公司之间分配一个已经确定的结果。",
        ],
      },
      {
        id: "s2",
        heading: "三种常见角色",
        paragraphs: [
          "第一种是被内定的中标方，通常提前掌握了技术要求和报价区间。第二种是陪标方，负责凑足法定家数，报价刻意抬高或文件刻意留有瑕疵。第三种是围标方，多份投标文件由同一批人制作，形成表面的竞争。",
          "这些角色分工并不需要串通秘密，很多时候靠的是行业内的默认默契。",
        ],
        points: [
          "投标文件是否在排版、措辞、甚至错别字上高度相似",
          "不同公司的报价是否呈现不自然的规律性差距",
          "中标方是否长期、高频地出现在同一类项目中",
        ],
      },
      {
        id: "s3",
        heading: "外部人能看到什么",
        paragraphs: [
          "公开的招标公告、中标结果公示和评审记录，是可以查到的。把这些信息公开的细节交叉比对——比如同一批公司反复同时出现、报价规律异常——往往能看出一些端倪。",
          "这些信息不足以定性，但足以让你明白：公告上的「充分竞争」有时只是一种形式。",
        ],
      },
    ],
    risks: [
      "形式合规的流程不必然代表充分竞争",
      "陪标与围标难以从单份文件直接判断",
      "公开信息交叉比对只能提供线索，不能作为定性依据",
    ],
    tips: [
      "查阅招标公告、中标公示与评审记录等公开信息",
      "交叉比对同一批公司出现的频率与报价规律",
      "关注投标文件在格式与措辞上的相似度",
      "对异常报价规律保持记录并长期观察",
    ],
  },
  {
    id: "gym-prepay",
    title: "健身房的预付费周期：为什么总在办卡旺季之后停业",
    category: "gray",
    categoryLabel: "灰色地带",
    summary:
      "年卡、私教课、储值卡，本质上都是把未来的服务提前变现。我们按一家门店的开业到停业周期，标出资金流与经营压力在哪一步失衡。",
    cover:
      "https://readdy.ai/api/search-image?query=dark%20cinematic%20abstract%20photograph%20of%20empty%20gym%20equipment%20silhouettes%20in%20a%20dim%20room%20with%20amber%20and%20crimson%20accent%20light%2C%20moody%20abandoned%20atmosphere%2C%20editorial%20grain%20texture%2C%20no%20text&width=800&height=600&seq=case-gym-01&orientation=landscape",
      coverAlt: "昏暗房间里空置的健身器械剪影，琥珀与红色点缀光",
    author: "调查组 · 何姐",
    date: "2025-05-06",
    updated: "2025-06-15",
    readTime: "7 分钟",
    tags: ["健身房", "预付费", "储值卡", "资金链"],
    heat: 5740,
    featured: false,
    sections: [
      {
        id: "s1",
        heading: "预付费的本质是提前拿到未来的钱",
        paragraphs: [
          "一家新店开业时，最大的一笔现金收入来自预售年卡和私教课包。这笔钱对应的是未来一整年的服务成本，但它在账面上看起来就是当期收入。",
          "当后续新增办卡的速度放缓，门店就必须用新收的钱去覆盖旧的成本，现金流开始变得脆弱。",
        ],
        points: [
          "年卡价格是否明显低于按月支付的合理区间",
          "私教课包是否要求一次性购买大量课时",
          "储值卡是否设置高额赠送额度刺激大额充值",
        ],
      },
      {
        id: "s2",
        heading: "停业为什么常发生在旺季之后",
        paragraphs: [
          "办卡旺季通常集中在年初和年中，这两个阶段现金流入最多。旺季结束后，收入放缓而房租、人力、器材分期等固定支出不变，压力集中显现。",
          "所以「刚办完卡就听说要装修」「突然更换运营方」这类消息，往往不是偶然。",
        ],
      },
      {
        id: "s3",
        heading: "办卡前的三个简单动作",
        paragraphs: [
          "第一，看看这家店开业多久、器械的磨损程度与生意状态是否匹配。第二，优先选择月付或季付，把风险控制在较短的周期内。第三，对于大额储值，问清楚余额是否可以无条件退还。",
          "这些动作花不了多少时间，但能显著降低你把钱押在别人经营能力上的比例。",
        ],
      },
    ],
    risks: [
      "预付费把未来服务成本提前变为当期收入，现金流结构脆弱",
      "大额储值与赠送额度会放大单次损失",
      "门店停业前常出现装修、换运营方等模糊信号",
    ],
    tips: [
      "优先选择月付或季付，缩短风险周期",
      "大额储值前确认余额退还条件",
      "观察门店开业时长、器械磨损与实际客流",
      "保留合同、付款凭证与卡内余额截图",
    ],
  },
];

export const featuredCaseIds = [
  "renovation-hidden-cost",
  "livestream-price-trick",
  "used-car-odometer",
];